import * as React from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, User, Lock } from "lucide-react";
import { joinWithEmail, AuthResultDto } from "@/lib/auth-api";
import { GOOGLE_RECAPTCHA_SITE_KEY } from "@/lib/config";
import { GoogleReCaptchaProvider, GoogleReCaptcha } from "react-google-recaptcha-v3";
import { GoogleRecaptchaAction } from "@anydo/core";
import { useLanguage } from "@/contexts/LanguageContext";

interface NewUserFormProps {
  email: string;
  showCaptcha: boolean;
  onBack: () => void;
  onSuccess: (result: AuthResultDto) => void;
}

const NewUserFormInner: React.FC<NewUserFormProps & { captchaToken: string }> = ({
  email,
  onBack,
  onSuccess,
  captchaToken,
}) => {
  const [name, setName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const nameRef = React.useRef<HTMLInputElement>(null);
  const { t } = useLanguage();

  React.useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !password.trim()) return;

    if (password.length < 6) {
      setError(t("auth.error.passwordTooShort"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const result = await joinWithEmail(
        email,
        name.trim(),
        password,
        captchaToken
          ? {
              action: GoogleRecaptchaAction.EmailRegistration,
              siteKey: GOOGLE_RECAPTCHA_SITE_KEY,
              token: captchaToken,
            }
          : undefined,
      );
      onSuccess(result);
    } catch {
      setError(t("auth.error.generic"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-full hover:bg-secondary transition-colors">
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </button>
        <h2 className="text-2xl font-bold text-foreground flex-1 text-center pe-10">
          {t("auth.createAccount.title")}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-foreground mb-1.5 block">{t("auth.email")}</label>
          <input
            type="email"
            value={email}
            disabled
            className="w-full px-4 py-3 rounded-lg border border-input bg-muted text-muted-foreground"
          />
        </div>

        <div>
          <label htmlFor="name" className="text-sm font-medium text-foreground mb-1.5 block">
            {t("auth.name")}
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              ref={nameRef}
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("auth.namePlaceholder")}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="text-sm font-medium text-foreground mb-1.5 block">
            {t("auth.password")}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("auth.passwordPlaceholder")}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              required
              minLength={6}
            />
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button
          type="submit"
          disabled={isLoading || !name.trim() || !password.trim()}
          className="w-full py-6 text-base font-semibold rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("auth.createAccount.cta")}
        </Button>
      </form>
    </div>
  );
};

export const NewUserForm: React.FC<NewUserFormProps> = (props) => {
  const [captchaToken, setCaptchaToken] = React.useState("");

  if (props.showCaptcha) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECAPTCHA_SITE_KEY} useEnterprise>
        <GoogleReCaptcha
          onVerify={setCaptchaToken}
          action={GoogleRecaptchaAction.EmailRegistration}
        />
        <NewUserFormInner {...props} captchaToken={captchaToken} />
      </GoogleReCaptchaProvider>
    );
  }

  return <NewUserFormInner {...props} captchaToken="" />;
};
